import { useMemo, useState, useEffect } from "react";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useHotelsConfig } from "../../context";
import { HotelsRawEventName, useEmitCoreHotelsRawEvent } from "../../tracking";
import type { GuestPickerComponentProps } from "./GuestPicker";

export const useGuestPicker = (props: GuestPickerComponentProps) => {
  const MAXIMUM_NUM_ROOMS = 10;
  const MAXIMUM_GUESTS_PER_ROOM = 6;
  const {
    onGuestSelectionHandler,
    onRoomsSelectionHandler,
    guests,
    rooms,
    config,
  } = props;
  const hotelConfig = useHotelsConfig();
  const enableRoomSelection =
    config?.enableRoomSelection ?? hotelConfig.enableRoomSelection;
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();

  const [adultCount, setAdultCount] = useState(guests.adults ?? 1);
  const [roomCount, setRoomCount] = useState(rooms ?? 1);
  const [childAgeArr, setChildAgeArr] = useState<number[]>(guests.children);

  const maximumRoomsReached = useMemo(() => {
    return roomCount >= MAXIMUM_NUM_ROOMS;
  }, [roomCount]);

  const emitGuestsSelectionOpened = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelGuestsSelectionOpened
  );
  const emitGuestsSelected = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelGuestsSelected
  );
  const emitRoomsSelected = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelRoomsSelected
  );

  useEffect(() => {
    setAdultCount(guests.adults);
  }, [guests.adults]);

  useEffect(() => {
    setChildAgeArr(guests.children);
  }, [guests.children]);

  useEffect(() => {
    setRoomCount(rooms ?? 1);
  }, [rooms]);

  const totalCount = useMemo(() => {
    return adultCount + childAgeArr.length;
  }, [adultCount, childAgeArr.length]);
  const maximumReached = useMemo(() => {
    return totalCount >= MAXIMUM_GUESTS_PER_ROOM * roomCount;
  }, [totalCount, roomCount]);

  const totalValidCount = useMemo(
    () => guests.adults + guests.children.length,
    [guests.adults, guests.children.length]
  );

  const resetGuestsToValidState = () => {
    setAdultCount(guests.adults);
    setChildAgeArr(guests.children);
  };

  const updateChildAge = (index: number, value: number) => {
    setChildAgeArr((prevArr) => {
      const newChildAgeArr = [...prevArr];
      newChildAgeArr[index] = value;
      return newChildAgeArr;
    });
  };

  const validateGuestPicker = useMemo(() => {
    return childAgeArr.every((age) => age != null);
  }, [childAgeArr]);

  const adjustAgeArray = (length: number) => {
    if (childAgeArr.length > length) {
      return childAgeArr.slice(0, length);
    } else if (childAgeArr.length < length) {
      return [...childAgeArr, ...Array(length - childAgeArr.length).fill(null)];
    }
    return childAgeArr;
  };

  const onGuestSelection = (value: { adults: number; children: number[] }) => {
    emitGuestsSelected({
      properties: {
        adults: value.adults,
        childrenAges: value.children,
      },
    });

    onGuestSelectionHandler(value, roomCount);
    if (enableRoomSelection === true) {
      emitRoomsSelected({
        properties: {
          rooms: roomCount,
        },
      });
      onRoomsSelectionHandler?.(roomCount);
    }
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      resetGuestsToValidState();
    } else {
      emitGuestsSelectionOpened({
        properties: {
          adults: adultCount,
          childrenAges: childAgeArr,
        },
      });
    }
  };

  return {
    context: {
      adultCount,
      roomCount,
      childCount: childAgeArr.length,
      childAgeArr,
      totalCount,
      maximumReached,
      minimumAdultCount: 1,
      showGuestPickerAsFullscreen: !isDesktopAndUp,
      guests,
      isGuestPickerValid: validateGuestPicker,
      maximumRoomsReached,
      enableRoomSelection,
    },
    handlers: {
      onRoomsSelectionHandler,
      onGuestSelection,
      onOpenChange,
      resetGuestsToValidState,
      onRoomsChange: setRoomCount,
      onAdultChange: setAdultCount,
      setChildCount: (count: number) => {
        setChildAgeArr(adjustAgeArray(count));
      },
      updateChildAge,
    },
    text: {
      roomTitle: t("core-hotels.search.guestPicker.rooms.title"),
      roomSubtitle: t("core-hotels.search.guestPicker.rooms.subtitle"),
      adultTitle: t("core-hotels.search.guestPicker.adult.title"),
      adultSubtitle: t("core-hotels.search.guestPicker.adult.subtitle"),
      applyButtonLabel: t("core-hotels.search.guestPicker.applyButton"),
      buttonLabel: enableRoomSelection
        ? `${t("core-hotels.search.guestPicker.buttonLabel", {
            count: totalValidCount,
          })}, ${t("core-hotels.availabilityPage.results.hotelCard.price.numberOfRooms", { count: roomCount })}`
        : t("core-hotels.search.guestPicker.buttonLabel", {
            count: totalValidCount,
          }),
      childCountTitle: t("core-hotels.search.guestPicker.child.title"),
      childCountSubtitle: t("core-hotels.search.guestPicker.child.subtitle"),
      childAgeLabel: (childIndex: number) =>
        t("core-hotels.search.guestPicker.child.childIndexLabel", {
          index: childIndex + 1,
        }),
      modalTitle: t("core-hotels.search.guestPicker.modalTitle"),
    },
  };
};
